/* ==========================================================================
   Markdown Parser - The Arcanopolis Archive
   Handles markdown parsing, frontmatter extraction, and cross-references
   ========================================================================== */

// Markdown parser with frontmatter support
const MarkdownParser = {
    // Parse markdown content with frontmatter
    parse(markdownContent) {
        const { frontmatter, content } = this.extractFrontmatter(markdownContent);
        const parsedContent = this.parseMarkdown(content);
        const crossReferences = this.extractCrossReferences(content);
        
        return {
            ...frontmatter,
            content: parsedContent,
            rawContent: content,
            crossReferences: crossReferences,
            id: this.generateId(frontmatter.title || 'untitled')
        };
    },
    
    // Extract frontmatter from markdown
    extractFrontmatter(content) {
        const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
        const match = content.match(frontmatterRegex);
        
        if (match) {
            const frontmatterText = match[1];
            const markdownContent = match[2];
            
            return {
                frontmatter: this.parseFrontmatter(frontmatterText),
                content: markdownContent
            };
        }
        
        return {
            frontmatter: {},
            content: content
        };
    },
    
    // Parse frontmatter YAML-like syntax
    parseFrontmatter(frontmatterText) {
        try {
            // Use js-yaml if available, otherwise fall back to simple parsing
            if (typeof jsyaml !== 'undefined') {
                return jsyaml.load(frontmatterText) || {};
            }
        } catch (error) {
            console.warn('Error parsing YAML frontmatter, falling back to simple parser:', error);
        }
        
        // Fallback simple parser
        const frontmatter = {};
        const lines = frontmatterText.split('\n');
        
        for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine || trimmedLine.startsWith('#')) continue;
            
            const colonIndex = trimmedLine.indexOf(':');
            if (colonIndex === -1) continue;
            
            const key = trimmedLine.substring(0, colonIndex).trim();
            let value = trimmedLine.substring(colonIndex + 1).trim();
            
            // Remove quotes if present
            if ((value.startsWith('"') && value.endsWith('"')) ||
                (value.startsWith("'") && value.endsWith("'"))) {
                value = value.slice(1, -1);
            }
            
            // Parse arrays
            if (value.startsWith('[') && value.endsWith(']')) {
                const arrayContent = value.slice(1, -1);
                frontmatter[key] = arrayContent.split(',').map(item => 
                    item.trim().replace(/^["']|["']$/g, '')
                );
            }
            // Parse objects (for relationships)
            else if (value.startsWith('{') && value.endsWith('}')) {
                try {
                    frontmatter[key] = JSON.parse(value);
                } catch (e) {
                    frontmatter[key] = value;
                }
            }
            // Parse arrays of objects (for relationships)
            else if (key === 'relationships' && value.includes('-')) {
                frontmatter[key] = this.parseRelationships(frontmatterText);
            }
            else {
                frontmatter[key] = value;
            }
        }
        
        return frontmatter;
    },
    
    // Parse relationships from frontmatter
    parseRelationships(frontmatterText) {
        const relationships = [];
        const lines = frontmatterText.split('\n');
        let inRelationships = false;
        let currentRelationship = {};
        
        for (const line of lines) {
            const trimmedLine = line.trim();
            
            if (trimmedLine === 'relationships:') {
                inRelationships = true;
                continue;
            }
            
            if (inRelationships) {
                if (trimmedLine.startsWith('-')) {
                    if (currentRelationship.target) {
                        relationships.push(currentRelationship);
                    }
                    currentRelationship = {};
                } else if (trimmedLine.startsWith('target:')) {
                    currentRelationship.target = trimmedLine.split(':')[1].trim().replace(/^["']|["']$/g, '');
                } else if (trimmedLine.startsWith('type:')) {
                    currentRelationship.type = trimmedLine.split(':')[1].trim().replace(/^["']|["']$/g, '');
                }
            }
        }
        
        if (currentRelationship.target) {
            relationships.push(currentRelationship);
        }
        
        return relationships;
    },
    
    // Enhanced markdown parsing using marked.js
    parseMarkdown(content) {
        // Use marked.js if available, otherwise fall back to simple parsing
        if (typeof marked !== 'undefined') {
            // Configure marked for our needs
            marked.setOptions({
                breaks: true,
                gfm: true,
                sanitize: false
            });
            
            // Add custom renderer for cross-references
            const renderer = new marked.Renderer();
            renderer.text = function(text) {
                // Convert [[cross-references]] to special spans
                return text.replace(/\[\[([^\]]+)\]\]/g, '<span class="cross-reference" data-ref="$1">$1</span>');
            };
            
            marked.setOptions({ renderer: renderer });
            
            return marked.parse(content);
        }
        
        // Fallback simple markdown parsing
        return this.simpleMarkdownParse(content);
    },
    
    // Simple markdown parsing fallback
    simpleMarkdownParse(content) {
        let parsed = content;
        
        // Headers
        parsed = parsed.replace(/^### (.*$)/gim, '<h3>$1</h3>');
        parsed = parsed.replace(/^## (.*$)/gim, '<h2>$1</h2>');
        parsed = parsed.replace(/^# (.*$)/gim, '<h1>$1</h1>');
        
        // Bold and italic
        parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');
        
        // Links
        parsed = parsed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
        
        // Cross-references (double brackets)
        parsed = parsed.replace(/\[\[([^\]]+)\]\]/g, '<span class="cross-reference" data-ref="$1">$1</span>');
        
        // Line breaks
        parsed = parsed.replace(/\n\n/g, '</p><p>');
        parsed = parsed.replace(/\n/g, '<br>');
        
        // Wrap in paragraph tags
        parsed = '<p>' + parsed + '</p>';
        
        return parsed;
    },
    
    // Extract cross-references from content
    extractCrossReferences(content) {
        const crossRefRegex = /\[\[([^\]]+)\]\]/g;
        const references = [];
        let match;
        
        while ((match = crossRefRegex.exec(content)) !== null) {
            references.push(match[1]);
        }
        
        return [...new Set(references)]; // Remove duplicates
    },
    
    // Generate unique ID from title
    generateId(title) {
        return title.toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '-')
            .substring(0, 50);
    },
    
    // Load and parse markdown file
    async loadMarkdownFile(filePath) {
        try {
            const baseUrl = window.SHOPIFY_ASSET_URL || '';
            const response = await fetch(`${baseUrl}${filePath}`);
            if (!response.ok) {
                throw new Error(`Failed to load ${filePath}`);
            }
            
            const content = await response.text();
            return this.parse(content);
        } catch (error) {
            console.error(`Error loading markdown file ${filePath}:`, error);
            return null;
        }
    }
};

// Content loader with manifest system
const ContentLoader = {
    manifest: null,
    loadedContent: new Map(),
    
    // Load manifest file
    async loadManifest() {
        try {
            const baseUrl = window.SHOPIFY_ASSET_URL || '';
            const response = await fetch(`${baseUrl}manifest.json`);
            if (!response.ok) {
                throw new Error('Failed to load manifest.json');
            }
            
            this.manifest = await response.json();
            console.log('📋 Manifest loaded:', this.manifest);
            return this.manifest;
        } catch (error) {
            console.error('Error loading manifest:', error);
            return null;
        }
    },
    
    // Load all content from manifest
    async loadAllContent() {
        if (!this.manifest) {
            await this.loadManifest();
        }
        
        if (!this.manifest) {
            console.error('No manifest available');
            return [];
        }
        
        const allContent = [];
        
        for (const [category, files] of Object.entries(this.manifest.categories)) {
            console.log(`📁 Loading ${category}: ${files.length} files`);
            
            for (const file of files) {
                const filePath = file;
                const content = await MarkdownParser.loadMarkdownFile(filePath);
                
                if (content) {
                    content.category = category;
                    content.filePath = filePath;
                    allContent.push(content);
                    this.loadedContent.set(content.id, content);
                }
            }
        }
        
        console.log(`✅ Loaded ${allContent.length} content items`);
        return allContent;
    },
    
    // Load content for a specific category
    async loadCategoryContent(category) {
        if (!this.manifest) {
            await this.loadManifest();
        }
        
        if (!this.manifest || !this.manifest.categories[category]) {
            return [];
        }
        
        const categoryContent = [];
        const files = this.manifest.categories[category];
        
        for (const file of files) {
            const filePath = `${category}/${file}`;
            const content = await MarkdownParser.loadMarkdownFile(filePath);
            
            if (content) {
                content.category = category;
                content.filePath = filePath;
                categoryContent.push(content);
                this.loadedContent.set(content.id, content);
            }
        }
        
        return categoryContent;
    },
    
    // Load content by ID
    async loadContentById(id) {
        if (this.loadedContent.has(id)) {
            return this.loadedContent.get(id);
        }
        
        // If not in cache, try to find and load it
        if (!this.manifest) {
            await this.loadManifest();
        }
        
        if (!this.manifest) {
            return null;
        }
        
        // Search through all categories for the file
        for (const [category, files] of Object.entries(this.manifest.categories)) {
            for (const file of files) {
                const filePath = file;
                const content = await MarkdownParser.loadMarkdownFile(filePath);
                
                if (content && content.id === id) {
                    content.category = category;
                    content.filePath = filePath;
                    this.loadedContent.set(id, content);
                    return content;
                }
            }
        }
        
        return null;
    },
    
    // Get cached content by ID
    getContentById(id) {
        return this.loadedContent.get(id);
    },
    
    // Search content
    searchContent(query, options = {}) {
        const { category, tags, type } = options;
        const allContent = Array.from(this.loadedContent.values());
        
        let results = allContent;
        
        // Filter by category
        if (category && category !== 'all') {
            results = results.filter(item => item.category === category);
        }
        
        // Filter by type
        if (type) {
            results = results.filter(item => item.type === type);
        }
        
        // Filter by tags
        if (tags && tags.length > 0) {
            results = results.filter(item => 
                item.tags && item.tags.some(tag => tags.includes(tag))
            );
        }
        
        // Simple text search
        if (query) {
            const lowerQuery = query.toLowerCase();
            results = results.filter(item => 
                item.title.toLowerCase().includes(lowerQuery) ||
                (item.content && item.content.toLowerCase().includes(lowerQuery)) ||
                (item.rawContent && item.rawContent.toLowerCase().includes(lowerQuery)) ||
                (item.tags && item.tags.some(tag => tag.toLowerCase().includes(lowerQuery)))
            );
        }
        
        return results;
    }
};

// Cross-reference manager
const CrossReferenceManager = {
    references: new Map(),
    
    // Initialize cross-references
    init(allItems) {
        this.references.clear();
        
        // Build reference map
        allItems.forEach(item => {
            if (item.title) {
                this.references.set(item.title.toLowerCase(), item);
            }
            if (item.id) {
                this.references.set(item.id, item);
            }
        });
        
        // Process cross-references
        this.processCrossReferences(allItems);
    },
    
    // Process cross-references for all items
    processCrossReferences(allItems) {
        allItems.forEach(item => {
            item.mentionedIn = [];
            item.mentions = item.crossReferences || [];
        });
        
        // Build mention relationships
        allItems.forEach(item => {
            if (item.crossReferences) {
                item.crossReferences.forEach(refTitle => {
                    if (refTitle) {
                        const referencedItem = this.references.get(refTitle.toLowerCase());
                        if (referencedItem) {
                            referencedItem.mentionedIn = referencedItem.mentionedIn || [];
                            referencedItem.mentionedIn.push({
                                title: item.title,
                                id: item.id,
                                category: item.category
                            });
                        }
                    }
                });
            }
        });
    },
    
    // Get related items for an item
    getRelatedItems(item, maxItems = 5) {
        const related = [];
        
        // Add items mentioned in this item
        if (item.crossReferences) {
            item.crossReferences.forEach(refTitle => {
                const referencedItem = this.references.get(refTitle.toLowerCase());
                if (referencedItem) {
                    related.push({
                        ...referencedItem,
                        relationship: 'mentioned'
                    });
                }
            });
        }
        
        // Add items that mention this item
        if (item.mentionedIn) {
            item.mentionedIn.forEach(mention => {
                const mentioningItem = this.references.get(mention.title.toLowerCase());
                if (mentioningItem) {
                    related.push({
                        ...mentioningItem,
                        relationship: 'mentions'
                    });
                }
            });
        }
        
        // Add items with similar tags
        if (item.tags) {
            const allItems = Array.from(this.references.values());
            const similarItems = allItems.filter(otherItem => {
                if (otherItem.id === item.id) return false;
                if (!otherItem.tags) return false;
                
                return otherItem.tags.some(tag => item.tags.includes(tag));
            });
            
            similarItems.forEach(similarItem => {
                if (!related.find(r => r.id === similarItem.id)) {
                    related.push({
                        ...similarItem,
                        relationship: 'similar'
                    });
                }
            });
        }
        
        return related.slice(0, maxItems);
    },
    
    // Create cross-reference links in HTML
    createCrossReferenceLinks(htmlContent) {
        return htmlContent.replace(
            /<span class="cross-reference" data-ref="([^"]+)">([^<]+)<\/span>/g,
            (match, refTitle, displayText) => {
                const referencedItem = this.references.get(refTitle.toLowerCase());
                if (referencedItem) {
                    return `<a href="content.html?id=${referencedItem.id}" class="cross-reference-link" data-tooltip="${referencedItem.title}">${displayText}</a>`;
                }
                return `<span class="cross-reference-unresolved" title="Reference not found: ${refTitle}">${displayText}</span>`;
            }
        );
    }
};

// Export for use in other modules
window.MarkdownParser = MarkdownParser;
window.CrossReferenceManager = CrossReferenceManager;
window.ContentLoader = ContentLoader;